import React, { useState, useMemo } from "react";
import styled from "@emotion/styled";
import Link from "next/link";
import {
  ImpactShopProductType,
  ImpactType,
  GLOBAL_TREES_SLUG,
  UK_TREES_SLUG,
  WILDFLOWER_SLUG,
  PEATLANDS_SLUG,
  SEAGRASS_SLUG,
} from "@ecologi/shared";
import { DialogModal } from "../DialogModal";
import { Button } from "../Button";
import { Heading } from "../Heading";
import { Typography } from "../Typography";

type QuestionNode = {
  id: string;
  question: string;
  options: Array<{
    label: string;
    next: string;
  }>;
};

const questionTree: Record<string, QuestionNode> = {
  goal: {
    id: "goal",
    question: "What's your goal?",
    options: [
      { label: "Funding tree planting", next: "tree-budget" },
      { label: "Offsetting emissions", next: "emission-type" },
      { label: "Funding habitat restoration", next: "habitat-budget" },
    ],
  },
  "tree-budget": {
    id: "tree-budget",
    question: "How do you want to fund trees?",
    options: [
      { label: "I have a specific budget", next: "tree-location" },
      {
        label: "I have a specific number of trees in mind",
        next: "tree-calculation",
      },
    ],
  },
  "tree-calculation": {
    id: "tree-calculation",
    question: "How often do you want to fund trees?",
    options: [
      { label: "Just once", next: "tree-location" },
      { label: "On a recurring basis", next: "fixed-amount-per" },
    ],
  },
  "fixed-amount-per": {
    id: "fixed-amount-per",
    question: "Recurring trees would be funded...",
    options: [
      { label: "Per client", next: "tree-location" },
      { label: "Per project", next: "tree-location" },
      { label: "Per product", next: "tree-location" },
      { label: "Per something else", next: "tree-location" },
    ],
  },
  "tree-location": {
    id: "tree-location",
    question: "Where do you want to fund trees?",
    options: [
      { label: "I prefer a local tree", next: "uk-trees" },
      { label: "I prefer a global tree", next: "global-trees" },
    ],
  },
  "emission-type": {
    id: "emission-type",
    question: "What type of emissions are you looking to offset?",
    options: [
      { label: "Utility consumption emissions", next: "measured" },
      { label: "Travel / delivery emissions", next: "measured" },
      { label: "Product emissions", next: "measured" },
      { label: "Scope 1/2 or 3 emissions", next: "measured" },
      { label: "Other", next: "measured" },
    ],
  },
  measured: {
    id: "measured",
    question: "Have you measured your emissions?",
    options: [
      { label: "I've measured my emissions", next: "carbon-projects" },
      { label: "I haven't measured my emissions", next: "schedule-call" },
    ],
  },
  "habitat-budget": {
    id: "habitat-budget",
    question: "How do you want to fund habitat restoration?",
    options: [
      { label: "I have a specific budget", next: "habitat-preference" },
      {
        label: "I have a specific habitat size in mind",
        next: "habitat-calculation",
      },
    ],
  },
  "habitat-calculation": {
    id: "habitat-calculation",
    question: "How often do you want to fund habitat restoration?",
    options: [
      { label: "Just once", next: "habitat-preference" },
      { label: "On a recurring basis", next: "habitat-fixed-amount-per" },
    ],
  },
  "habitat-fixed-amount-per": {
    id: "habitat-fixed-amount-per",
    question: "Recurring habitat would be funded...",
    options: [
      { label: "Per client", next: "habitat-preference" },
      { label: "Per project", next: "habitat-preference" },
      { label: "Per product", next: "habitat-preference" },
      { label: "Per something else", next: "habitat-preference" },
    ],
  },
  "habitat-preference": {
    id: "habitat-preference",
    question: "Fund habitat restoration at land or sea?",
    options: [
      { label: "I prefer land", next: "land-habitat-products" },
      { label: "I prefer sea", next: "seagrass" },
    ],
  },
};

const INITIAL_STEP = "goal";

const TERMINAL_NODES: Record<string, string> = {
  "carbon-projects": "Carbon credit projects",
  "schedule-call": "Speak to the team",
  "uk-trees": "UK tree planting",
  "global-trees": "Global tree planting",
  "land-habitat-products": "Land habitat restoration",
  seagrass: "Sea habitat restoration",
};

const isTerminalNode = (step: string): boolean => step in TERMINAL_NODES;

const MAX_QUESTION_PATH_LENGTH = 2000;

type HistoryEntry = {
  step: string;
  answer: string;
};

type QuestionPathResult = {
  questionPath: string;
  questionPathTruncatedAtStart: boolean;
};

const formatQuestionPath = (
  historyEntries: HistoryEntry[],
  currentStep?: string,
  currentAnswer?: string
): QuestionPathResult => {
  const entries = [...historyEntries];

  if (currentStep && currentAnswer) {
    entries.push({ step: currentStep, answer: currentAnswer });
  }

  let pathObject: Record<string, string> = {};
  entries.forEach((entry) => {
    pathObject[entry.step] = entry.answer;
  });

  let result = JSON.stringify(pathObject);
  let truncated = false;

  while (result.length > MAX_QUESTION_PATH_LENGTH && entries.length > 1) {
    entries.shift();
    pathObject = {};
    entries.forEach((entry) => {
      pathObject[entry.step] = entry.answer;
    });
    result = JSON.stringify(pathObject);
    truncated = true;
  }

  return {
    questionPath: result,
    questionPathTruncatedAtStart: truncated,
  };
};

const generateSessionId = (): string => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const QuestionHeading = styled(Heading)`
  text-align: center;
  margin-bottom: 8px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: block;
  width: 100%;
  padding: 20px 24px;
  border: 1px solid ${({ theme }) => theme.colors.cloudDark};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 1.75rem;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.mist};
    border-color: ${({ theme }) => theme.colors.forest};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
`;

const BackButton = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.charcoal};
  padding: 8px;
  border-radius: 6px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 2px;
    background: ${({ theme }) => theme.colors.mist};
  }
`;

const ProjectsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media ${({ theme }) => theme.bp.tabletUp} {
    max-height: 400px;
    flex: initial;
  }
`;

const ProjectCard = styled.a`
  display: block;
  padding: 20px 24px;
  border: 1px solid ${({ theme }) => theme.colors.cloudDark};
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.mist};
    border-color: ${({ theme }) => theme.colors.forest};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
`;

const ProjectTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 6px;
`;

const ProjectSnippet = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoalLight};
`;

const ScheduleCallContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
  padding: 24px 0;
`;

const ScheduleCallText = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoal};
`;

export type UnsureWhatToFundModalProps = {
  isOpen: boolean;
  onClose: () => void;
  products: ImpactShopProductType[];
  onAnalyticsEvent?: (
    eventName: string,
    props: Record<string, string | number | boolean>
  ) => void;
};

export const UnsureWhatToFundModal = ({
  isOpen,
  onClose,
  products,
  onAnalyticsEvent,
}: UnsureWhatToFundModalProps) => {
  const [currentStep, setCurrentStep] = useState<string>(INITIAL_STEP);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [sessionId] = useState(() => generateSessionId());

  const carbonProjects = useMemo(
    () =>
      products.filter(
        (product) =>
          product.productType === ImpactType.CARBON_OFFSETS ||
          product.productType === ImpactType.CARBON_REMOVAL
      ),
    [products]
  );

  const ukTreesProduct = useMemo(
    () => products.find((product) => product.slug === UK_TREES_SLUG),
    [products]
  );

  const globalTreesProduct = useMemo(
    () => products.find((product) => product.slug === GLOBAL_TREES_SLUG),
    [products]
  );

  const wildflowerProduct = useMemo(
    () => products.find((product) => product.slug === WILDFLOWER_SLUG),
    [products]
  );

  const peatlandProduct = useMemo(
    () => products.find((product) => product.slug === PEATLANDS_SLUG),
    [products]
  );

  const seagrassProduct = useMemo(
    () => products.find((product) => product.slug === SEAGRASS_SLUG),
    [products]
  );

  const handleClose = () => {
    if (
      onAnalyticsEvent &&
      (history.length > 0 || currentStep !== INITIAL_STEP)
    ) {
      let result: QuestionPathResult;
      const terminalNodeReached = isTerminalNode(currentStep);

      if (terminalNodeReached) {
        result = formatQuestionPath(
          history,
          currentStep,
          TERMINAL_NODES[currentStep]
        );
      } else {
        result =
          history.length > 0
            ? formatQuestionPath(history)
            : { questionPath: "{}", questionPathTruncatedAtStart: false };
      }

      onAnalyticsEvent("impactShopUnsureWhatToFundModalClosed", {
        questionPath: result.questionPath,
        questionPathTruncatedAtStart: result.questionPathTruncatedAtStart,
        terminalNodeReached,
        sessionId,
      });
    }

    onClose();
  };

  const handleOptionSelect = (next: string, selectedAnswer: string) => {
    setHistory((prev) => [
      ...prev,
      { step: currentStep, answer: selectedAnswer },
    ]);
    setCurrentStep(next);

    if (isTerminalNode(next) && onAnalyticsEvent) {
      const { questionPath, questionPathTruncatedAtStart } = formatQuestionPath(
        [...history, { step: currentStep, answer: selectedAnswer }],
        next,
        TERMINAL_NODES[next]
      );

      onAnalyticsEvent("impactShopUnsureWhatToFundRecommendation", {
        questionPath,
        questionPathTruncatedAtStart,
        terminalNodeReached: true,
        sessionId,
      });
    }
  };

  const handleBack = () => {
    if (history.length > 0) {
      const previousEntry = history[history.length - 1];
      const previousStep = previousEntry.step;

      if (onAnalyticsEvent) {
        const { questionPath, questionPathTruncatedAtStart } =
          formatQuestionPath(history);

        const terminalNodeReached =
          history.some((entry) => isTerminalNode(entry.step)) ||
          isTerminalNode(currentStep);

        onAnalyticsEvent("impactShopUnsureWhatToFundModalBackClicked", {
          questionPath,
          questionPathTruncatedAtStart,
          terminalNodeReached,
          sessionId,
        });
      }

      setHistory((prev) => prev.slice(0, -1));
      setCurrentStep(previousStep);
    }
  };

  const currentQuestion = questionTree[currentStep];
  const canGoBack = history.length > 0;

  const renderQuestionView = () => (
    <ContentWrapper>
      <QuestionHeading size={5} as="h2">
        {currentQuestion.question}
      </QuestionHeading>
      <OptionsContainer>
        {currentQuestion.options.map((option) => (
          <OptionButton
            key={option.label}
            onClick={() => handleOptionSelect(option.next, option.label)}
          >
            {option.label}
          </OptionButton>
        ))}
      </OptionsContainer>
    </ContentWrapper>
  );

  const renderCarbonProjectsView = () => (
    <ContentWrapper>
      <QuestionHeading size={4} as="h2">
        Carbon credit projects
      </QuestionHeading>
      <Typography use="body1">
        Based on your answers, here are the carbon credit projects you can fund:
      </Typography>
      <ProjectsList>
        {carbonProjects.map((project) => (
          <Link
            key={project.slug}
            href={`/impact-shop/${project.slug}`}
            passHref
            legacyBehavior
          >
            <ProjectCard onClick={handleClose}>
              <ProjectTitle use="subtitle2">
                {project.display.title}
              </ProjectTitle>
              <ProjectSnippet use="body1">
                {project.display.snippet}
              </ProjectSnippet>
            </ProjectCard>
          </Link>
        ))}
      </ProjectsList>
    </ContentWrapper>
  );

  const renderScheduleCallView = () => (
    <ContentWrapper>
      <ScheduleCallContainer>
        <QuestionHeading size={4} as="h2">
          Speak to the team
        </QuestionHeading>
        <ScheduleCallText use="subtitle2">
          Our team can help you measure your emissions and find the right
          projects to fund.
        </ScheduleCallText>
        <Button
          text="Schedule a call"
          href="/page/schedule-a-call"
          onClick={handleClose}
          primary
        />
      </ScheduleCallContainer>
    </ContentWrapper>
  );

  const renderTreeProductView = (
    product: ImpactShopProductType | undefined,
    title: string
  ) => {
    if (!product) {
      return (
        <ContentWrapper>
          <QuestionHeading size={4} as="h2">
            {title}
          </QuestionHeading>
          <Typography use="body1">Product not available.</Typography>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={4} as="h2">
          {title}
        </QuestionHeading>
        <Typography use="body1">
          Based on your answers, here&apos;s a tree planting project for you:
        </Typography>
        <ProjectsList>
          <Link href={`/impact-shop/${product.slug}`} passHref legacyBehavior>
            <ProjectCard onClick={handleClose}>
              <ProjectTitle use="subtitle2">
                {product.display.title}
              </ProjectTitle>
              <ProjectSnippet use="body1">
                {product.display.snippet}
              </ProjectSnippet>
            </ProjectCard>
          </Link>
        </ProjectsList>
      </ContentWrapper>
    );
  };

  const renderLandHabitatProductsView = () => {
    const landProducts = [wildflowerProduct, peatlandProduct].filter(
      (p): p is ImpactShopProductType => p !== undefined
    );

    if (landProducts.length === 0) {
      return (
        <ContentWrapper>
          <QuestionHeading size={4} as="h2">
            Land habitat restoration
          </QuestionHeading>
          <Typography use="body1">
            No land habitat products available.
          </Typography>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={4} as="h2">
          Land habitat restoration
        </QuestionHeading>
        <Typography use="body1">
          Based on your answers, here are land habitat restoration projects you
          can fund:
        </Typography>
        <ProjectsList>
          {landProducts.map((product) => (
            <Link
              key={product.slug}
              href={`/impact-shop/${product.slug}`}
              passHref
              legacyBehavior
            >
              <ProjectCard onClick={handleClose}>
                <ProjectTitle use="subtitle2">
                  {product.display.title}
                </ProjectTitle>
                <ProjectSnippet use="body1">
                  {product.display.snippet}
                </ProjectSnippet>
              </ProjectCard>
            </Link>
          ))}
        </ProjectsList>
      </ContentWrapper>
    );
  };

  const renderSeaHabitatProductView = () => {
    if (!seagrassProduct) {
      return (
        <ContentWrapper>
          <QuestionHeading size={4} as="h2">
            Sea habitat restoration
          </QuestionHeading>
          <Typography use="body1">Product not available.</Typography>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={4} as="h2">
          Sea habitat restoration
        </QuestionHeading>
        <Typography use="body1">
          Based on your answers, here is a sea habitat restoration project you
          can fund:
        </Typography>
        <ProjectsList>
          <Link
            href={`/impact-shop/${seagrassProduct.slug}`}
            passHref
            legacyBehavior
          >
            <ProjectCard onClick={handleClose}>
              <ProjectTitle use="subtitle2">
                {seagrassProduct.display.title}
              </ProjectTitle>
              <ProjectSnippet use="body1">
                {seagrassProduct.display.snippet}
              </ProjectSnippet>
            </ProjectCard>
          </Link>
        </ProjectsList>
      </ContentWrapper>
    );
  };

  const renderContent = () => {
    if (currentStep === "carbon-projects") {
      return renderCarbonProjectsView();
    }
    if (currentStep === "schedule-call") {
      return renderScheduleCallView();
    }
    if (currentStep === "uk-trees") {
      return renderTreeProductView(ukTreesProduct, "UK tree planting");
    }
    if (currentStep === "global-trees") {
      return renderTreeProductView(globalTreesProduct, "Global tree planting");
    }
    if (currentStep === "land-habitat-products") {
      return renderLandHabitatProductsView();
    }
    if (currentStep === "seagrass") {
      return renderSeaHabitatProductView();
    }
    return renderQuestionView();
  };

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={handleClose}
      dataTestId="unsure-what-to-fund-modal"
      headerLeft={
        canGoBack ? (
          <BackButton
            onClick={handleBack}
            aria-label="Go back to previous question"
            data-testid="unsure-what-to-fund-back-button"
          >
            ← Back
          </BackButton>
        ) : undefined
      }
    >
      {renderContent()}
    </DialogModal>
  );
};
